import Keycloak from 'keycloak-js';

// Candidate authentication via Keycloak. Candidates are a per-tenant population,
// so the realm/client come from the environment (and can be overridden per
// tenant). Social login (Google, Facebook, ...) is handled by Keycloak identity
// brokering, reached by passing an idpHint to login().
//
// The session is persisted via the refresh token so it survives page reloads and
// deep links without a silent-SSO iframe (which fails cross-domain / with
// third-party cookies blocked). Trade-off: refresh tokens live in localStorage,
// so the app must stay XSS-safe. Tokens are dropped on logout or refresh failure.

const STORAGE_KEY = 'hp_candidate_auth';

let keycloak = null;
let initialized = false;

// Language for the Keycloak-hosted pages (login, register, password, TOTP). The
// realm has internationalization enabled with German as its default; this makes
// the pages follow the language the candidate selected in the portal rather than
// the browser's Accept-Language. Defaults to German until the portal sets it.
let uiLocale = 'de';

// A redirect_uri must not contain a fragment. Keycloak returns its response
// (code/state/iss) in the fragment, so reusing window.location.href verbatim
// would feed a fragment back as the redirect_uri — breaking the token exchange
// and causing an endless login redirect loop. Always strip the fragment.
function stripFragment(url) {
  const value = url || window.location.href;
  const hashIndex = value.indexOf('#');
  return hashIndex === -1 ? value : value.substring(0, hashIndex);
}

// The build-time defaults every tenant shares. A tenant only diverges when it
// has been given its own dedicated client (a paid "custom CI" arrangement),
// stored server-side and fetched at runtime by buildConfig().
function defaultConfig() {
  return {
    url: process.env.VUE_APP_KEYCLOAK_URL,
    realm: process.env.VUE_APP_KEYCLOAK_REALM || 'candidates',
    clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID || 'career-portal-spa'
  };
}

// Resolves the Keycloak client config for the current tenant. It asks the API
// for per-tenant overrides (keyed by the portal URL) and layers any it returns
// on top of the build-time defaults. The client id is therefore no longer fixed
// at build time: a tenant with a dedicated client is switched purely by data,
// no rebuild. Any failure (API down, no override) falls back to the defaults so
// the portal always loads.
async function buildConfig() {
  const config = defaultConfig();

  try {
    const response = await fetch(
      `${process.env.VUE_APP_API_URL}/auth/config?url=${encodeURIComponent(
        window.location.href
      )}`
    );

    if (response.ok) {
      const overrides = await response.json();
      if (overrides) {
        if (overrides.url) {
          config.url = overrides.url;
        }
        if (overrides.realm) {
          config.realm = overrides.realm;
        }
        if (overrides.clientId) {
          config.clientId = overrides.clientId;
        }
      }
    }
  } catch (e) {
    // Keep the build-time defaults if the override lookup fails.
    console.warn('[AuthService] Falling back to default auth config:', e);
  }

  return config;
}

function saveTokens() {
  if (!keycloak || !keycloak.refreshToken) {
    return;
  }

  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        token: keycloak.token,
        refreshToken: keycloak.refreshToken,
        idToken: keycloak.idToken
      })
    );
  } catch (e) {
    // Storage unavailable (private mode / quota); session just won't persist.
  }
}

function loadTokens() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function clearTokens() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

export default {
  // Initializes Keycloak once, restoring a persisted session when present, and
  // reports whether the user is signed in.
  async init() {
    if (initialized) {
      return this.isAuthenticated();
    }

    keycloak = new Keycloak(await buildConfig());

    // Keep the persisted tokens in sync as Keycloak refreshes them.
    keycloak.onAuthSuccess = () => saveTokens();
    keycloak.onAuthRefreshSuccess = () => saveTokens();
    keycloak.onAuthLogout = () => clearTokens();
    keycloak.onTokenExpired = () => {
      keycloak
        .updateToken(30)
        .then((refreshed) => {
          if (refreshed) {
            saveTokens();
          }
        })
        .catch(() => clearTokens());
    };

    const stored = loadTokens();

    try {
      // Restore any persisted session; init() also processes a login response in
      // the URL after a redirect back from Keycloak. No silent-SSO iframe.
      let authenticated = await keycloak.init({
        token: stored ? stored.token : undefined,
        refreshToken: stored ? stored.refreshToken : undefined,
        idToken: stored ? stored.idToken : undefined,
        pkceMethod: 'S256',
        checkLoginIframe: false
      });

      if (keycloak.authenticated) {
        // Fresh login or a still-valid restored session: make sure the access
        // token is current and persist it.
        try {
          await keycloak.updateToken(30);
          saveTokens();
          authenticated = true;
        } catch (e) {
          clearTokens();
          authenticated = false;
        }
      } else if (stored && stored.refreshToken) {
        // Restored access token was expired: try to recover via the refresh token.
        try {
          await keycloak.updateToken(-1);
          saveTokens();
          authenticated = keycloak.authenticated === true;
        } catch (e) {
          clearTokens();
          authenticated = false;
        }
      }

      initialized = true;
      return authenticated;
    } catch (e) {
      // If Keycloak is unreachable or the stored session is invalid, treat the
      // user as anonymous rather than blocking the portal.
      console.error('[AuthService] Keycloak init failed:', e);
      clearTokens();
      initialized = true;
      return false;
    }
  },

  // Sets the language used for the Keycloak-hosted pages. Accepts either a bare
  // subtag ('de') or a full tag ('de-DE'); only the primary subtag is used.
  setLocale(locale) {
    if (locale) {
      uiLocale = locale.substring(0, 2);
    }
  },

  login(redirectUri) {
    if (!keycloak) {
      return;
    }

    keycloak.login({ redirectUri: stripFragment(redirectUri), locale: uiLocale });
  },

  // Jumps straight to a brokered identity provider (e.g. 'google', 'facebook').
  loginWith(idpHint, redirectUri) {
    if (!keycloak) {
      return;
    }

    keycloak.login({ idpHint, redirectUri: stripFragment(redirectUri), locale: uiLocale });
  },

  register(redirectUri) {
    if (!keycloak) {
      return;
    }

    keycloak.register({ redirectUri: stripFragment(redirectUri), locale: uiLocale });
  },

  // Starts Keycloak's TOTP setup as an application-initiated action. Keycloak
  // shows its (hosted) QR/verify page, provisions the credential, and redirects
  // back to redirectUri. The secret never touches this app.
  enableTotp(redirectUri) {
    if (!keycloak) {
      return;
    }

    keycloak.login({
      action: 'CONFIGURE_TOTP',
      redirectUri: stripFragment(redirectUri),
      locale: uiLocale
    });
  },

  // Starts Keycloak's change-password flow as an application-initiated action.
  // maxAge: 0 forces re-authentication, so the candidate must confirm their
  // current password first. Keycloak validates the new password against the
  // realm policy and stores it; the password never touches this app.
  changePassword(redirectUri) {
    if (!keycloak) {
      return;
    }

    keycloak.login({
      action: 'UPDATE_PASSWORD',
      maxAge: 0,
      redirectUri: stripFragment(redirectUri),
      locale: uiLocale
    });
  },

  logout(redirectUri) {
    clearTokens();

    if (!keycloak) {
      return;
    }

    keycloak.logout({ redirectUri: redirectUri || window.location.origin });
  },

  isAuthenticated() {
    return !!(keycloak && keycloak.authenticated);
  },

  get candidateId() {
    return keycloak && keycloak.tokenParsed ? keycloak.tokenParsed.sub : null;
  },

  // Returns a valid access token, refreshing it first if it is close to expiry.
  async getToken() {
    if (!keycloak) {
      return null;
    }

    try {
      const refreshed = await keycloak.updateToken(30);
      if (refreshed) {
        saveTokens();
      }
    } catch (e) {
      // Token could not be refreshed; the caller will handle the 401.
      clearTokens();
    }

    return keycloak.token || null;
  }
};
